<template>
    <div
        v-click-outside="close"
        class="form-group"
        @keyup.esc="close"
        @keydown.enter.stop.prevent="selectCurrent"
        @keydown.tab="pressTab"
        @keydown.backspace="removeLastElem"
        @keyup.down="incrementPointer"
        @keyup.up="decrementPointer"
    >
        <div class="position-relative">
            <fieldset
                :id="id"
                class="form-control cursor-default base-select pt-2"
                :class="{'is-invalid': errors !== undefined}"
                :disabled="saving || disabled"
                @click="clickIn"
            >
                <div
                    class="w-100"
                    style="overflow: hidden"
                >
                    <template v-if="multiple">
                        <span
                            v-for="(selectedValue, key) of selected"
                            :key="key"
                            class="p-1 mr-1 rounded badge badge-primary"
                            :class="{'badge-gray' : disabled}"
                            @click="removeSelected(key)"
                        >
                            {{ findLabel(selectedValue) }}
                            <i
                                v-if="!disabled && !readonly"
                                class="fa fa-times ml-2 cursor-pointer"
                            />
                        </span>
                    </template>

                    <input
                        ref="searchInput"
                        v-model="display"
                        type="text"
                        :style="{width: inputWidth}"
                        :readonly="!useSearch || readonly"
                        @focus="open"
                        @click="clickIn"
                        @input="input($event.target.value)"
                    >
                </div>
            </fieldset>

            <label
                :for="id"
                :class="{active: searching || (!multiple && display) || selected.length}"
                @click="clickIn"
            >
                <slot />
            </label>

            <template v-if="!readonly">
                <transition name="fade">
                    <i
                        v-if="searching && useSearch && !filtering"
                        key="down"
                        class="select-arrow fa fa-search"
                    />

                    <i
                        v-else-if="searching && useSearch && filtering"
                        key="down"
                        class="select-arrow fa fa-circle-notch fa-spin"
                    />

                    <i
                        v-else
                        key="search"
                        class="select-arrow fa fa-chevron-down"
                    />
                </transition>

                <div class="persist-element">
                    <i
                        v-if="saving"
                        class="fa fa-spin fa-circle-notch"
                    />

                    <i
                        v-else-if="saved"
                        class="fa fa-check text-success"
                    />
                </div>
            </template>
        </div>

        <div
            v-if="searching"
            class="select-dropdown border border-top-0 rounded-bottom"
        >
            <div
                v-for="(option, key) in filteredOptions"
                :key="key"
                class="dropdown-item cursor-default rounded-0"
                :class="{active: key === pointer, disabled: isDisabled(option)}"
                @click.stop="select(option)"
                @mouseover="pointer = key"
                @mouseout="pointer = pointer === key ? null : pointer"
            >
                {{ option.label }}
            </div>
        </div>

        <base-error :errors="errors" />
    </div>
</template>

<script>
    import FormElement from '../../mixins/FormElement'
    import BaseError from './BaseError'

    export default {
        name: 'BaseSelect',

        components: { BaseError },

        mixins: [FormElement],

        props: {
            value: {type: [String, Number, Array, Boolean], default: null},
            options: {type: Array, default: () => []},
            src: {type: String, default: null},
            fetched: {type: Array, default: () => []},
            disabled: Boolean,
            multiple: Boolean,
            noSearch: Boolean,
            readonly: Boolean,
        },

        data: function () {
            return {
                searching: false,
                display: null,
                filteredOptions: _.cloneDeep(this.options),
                fetchedOptions: _.cloneDeep(this.fetched),
                pointer: null,
                filtering: false,
            }
        },

        computed: {
            /**
             * Width adaptative de l'input
             * @return String
             */
            inputWidth: function () {
                return ((this.display || '').length + 1) + 'em'
            },

            /**
             * utilise la recherche
             */
            useSearch: function () {
                return (!this.noSearch && this.options.length >  5) || this.src !== null
            },

            /**
             * Get selected values for multiple
             */
            selected: function () {
                return this.multiple && this.value ? this.value : []
            },

            /**
             * label à afficher
             * @return String
             */
            label: function () {
                let val = this.value !== null && this.value !== undefined ? this.value : ''
                return this.multiple ? '' : this.findLabel(val)
            },
        },

        watch: {
            /**
             * Changements apportés sur la valeur
             */
            value: function () {
                if (!this.multiple) {
                    this.searching = false
                }
                this.display = this.label
            },

            fetched: function () {
                this.fetchedOptions = this.fetchedOptions.filter(option => option.fetched)
                    .concat(_.cloneDeep(this.fetched))
            },

            /**
             * Mise a jours des options filtrés lors du changements des options
             */
            options: function (value, old) {
                if (_.differenceBy(value, old, 'value').length > 0) {
                    this.initOptions()
                }
                this.display = this.label
            },
        },

        created: function () {
            this.display = this.label
        },

        methods: {
            /**
             * retrouve le label d'une option à partir de sa valeur
             * @param value
             * @return String
             */
            findLabel: function (value) {
                let option = this.fetchedOptions.find(option => option.value === value)

                if (!option) {
                    option = this.options.find(option => option.value === value)
                }
                if (!option && typeof value === 'boolean') {
                    option = this.fetchedOptions.find(option => option.value === (value ? 1 : 0))
                }
                if (!option && typeof value === 'boolean') {
                    option = this.options.find(option => option.value === (value ? 1 : 0))
                }

                return option ? option.label : ''
            },

            /**
             * Ouvre la recherche
             */
            open: function () {
                if (!this.searching && !this.readonly) {
                    if (this.useSearch) {
                        this.display = ''
                    }

                    this.searching = true

                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus()
                    }

                    this.initOptions()
                }
            },

            /**
             * Click sur le select
             */
            clickIn: function () {
                if (!this.disabled && !this.readonly) {
                    if (!this.searching) {
                        this.open()
                    } else if (!this.useSearch && !this.multiple) {
                        this.close()
                    } else if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus()
                    }
                }
            },

            /**
             * Ferme la recherche
             */
            close: function () {
                if (this.searching) {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.blur()
                    }

                    this.searching = false
                    this.display = this.label
                    this.pointer = null
                }
            },

            /**
             * Triggered when input for search
             */
            input: function (value) {
                this.filtering = true
                this.filter(value)
            },

            /**
             * Filtre les options en fonction de la recherche
             * @param value
             */
            filter: _.debounce(async function (value) {
                let options = this.src ? await this.fetchFromSource(value) : []

                this.filteredOptions = _.cloneDeep(this.options)
                    .filter(option => (option.label || '').toLowerCase().includes(value.toLowerCase()))
                    .concat(options)
                    .filter(option => option.is_actif || option.is_actif === undefined)

                this.pointer = null
                this.filtering = false
            }, 500),

            /**
             * Initialise les options
             */
            initOptions: async function () {
                let options = this.src ? await this.fetchFromSource('') : []

                this.filteredOptions = _.cloneDeep(this.options)
                    .concat(options)
                    .filter(option => option.is_actif || option.is_actif === undefined)
            },

            /**
             * Sélectionne une option
             */
            select: function (option) {
                if (option.fetched) {
                    this.fetchedOptions.push(option)
                }

                if (this.multiple) {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus()
                    }

                    let values = this.selected
                    values.push(option.value)
                    this.$emit('input', values)
                    this.persist(values)
                    this.initOptions()
                } else {
                    this.$emit('input', option.value)
                    this.persist(option.value)
                }
            },

            /**
             * Supprime une option du multiselect
             */
            removeSelected: function (key) {
                let selected = this.selected
                selected.splice(key, 1)
                this.$emit('input', selected)
            },

            /**
             * Supprime le dernier élément sélectionné
             */
            removeLastElem: function () {
                if (this.display === '' && this.selected.length) {
                    this.removeSelected(this.selected.length - 1)
                }
            },

            /**
             * Retourne vrai si l'option doit être disabled
             */
            isDisabled: function (option) {
                if (this.multiple) {
                    return this.selected.includes(option.value)
                } else {
                    return option.value === this.value || this.value === ''
                        && (option.value === null || option.value === undefined)
                }
            },

            /**
             * Sélectionne le pointer
             */
            selectCurrent: function () {
                if (this.pointer !== null)  {
                    this.select(this.filteredOptions[this.pointer])
                    this.pointer = null
                }
            },

            /**
             * Action lors d'une tabulation
             */
            pressTab: function () {
                this.selectCurrent()
                this.close()
            },

            /**
             * Increment le pointer
             */
            incrementPointer: function () {
                if (this.pointer < this.filteredOptions.length - 1) {
                    let i = this.pointer === null ? -1 : this.pointer

                    do {
                        i++
                    } while (i < this.filteredOptions.length && this.isDisabled(this.filteredOptions[i]))

                    if (i < this.filteredOptions.length) {
                        this.pointer = i
                    }
                }
            },

            /**
             * Décrémente le pointer
             */
            decrementPointer: function () {
                if (this.pointer > 0) {
                    let i = this.pointer

                    do {
                        i--
                    } while (i >= 0 && this.isDisabled(this.filteredOptions[i]))

                    if (i >= 0) {
                        this.pointer = i
                    }
                }
            },

            /**
             * Get options from source
             */
            fetchFromSource: async function (search) {
                this.filtering = true
                try {

                    const response = await axios.get(this.src, {params: {search}})

                    let options = response.data.data

                    for (let option of options) {
                        option.fetched = true
                    }

                    return options
                } catch (e) {
                    return []
                } finally {
                    this.filtering = false
                }
            }
        }
    }
</script>

<style scoped lang="scss">
.badge {
    &:hover {
        cursor: pointer;
    }
}
.is-invalid {
    .form-control {
        border-left: 2px solid red !important;
    }
}
</style>
